/* Weight sort — rank generated documents by parcel weight status (from Database A)
 * so the team spots invoices with un-weighed parcels instantly. */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { Scale } from "lucide-react";

const MODES = [
  ["latest", "Latest"],
  ["missing", "No weight first"],
  ["weighed", "Weighed first"],
  ["only", "Only no-weight"],
];

export const useWeightStatus = (keys) => {
  const [st, setSt] = useState({});
  const sig = [...new Set(keys.filter(Boolean).map(k => String(k).toUpperCase()))].sort().join(",");
  useEffect(() => {
    if (!sig) return;
    axios.get(`${API}/packing-list/weight-status`, { headers: getAuthHeader(), params: { keys: sig } })
      .then(r => setSt(r.data || {})).catch(() => {});
  }, [sig]);
  return st;
};

export const useWeightMode = (table) => {
  const lsKey = `wtSort:${table}`;
  const [mode, setMode] = useState(() => localStorage.getItem(lsKey) || "latest");
  return [mode, (m) => { localStorage.setItem(lsKey, m); setMode(m); }];
};

const rank = (s) => (!s || !s.total ? 1 : s.missing === s.total ? 3 : s.missing > 0 ? 2 : 0);

// Sort/filter a list of entries; keyOf(entry) → invoice no.
export const useWeightSorted = (list, st, mode, keyOf) => useMemo(() => {
  if (mode === "latest") return list;
  const get = (e) => st[String(keyOf(e) || "").toUpperCase()];
  const arr = mode === "only" ? list.filter(e => (get(e)?.missing || 0) > 0) : [...list];
  return arr.map((e, i) => [e, i]).sort(([a, ia], [b, ib]) => {
    const sa = get(a), sb = get(b);
    const d = mode === "weighed" ? rank(sa) - rank(sb) : rank(sb) - rank(sa);
    return d || ((sb?.missing || 0) - (sa?.missing || 0)) * (mode === "weighed" ? -1 : 1) || ia - ib;
  }).map(([e]) => e);
}, [list, st, mode, keyOf]);

export const WeightSortToggle = ({ mode, setMode, st, testId }) => {
  const nMissing = Object.values(st).filter(s => s.missing > 0).length;
  return (
    <div className="inline-flex items-center rounded-full border border-slate-300 bg-white overflow-hidden text-[11px]" data-testid={`wt-sort-${testId}`}
         title="Sort by parcel weight status (Database A)">
      <span className="pl-2 pr-1 text-slate-500 inline-flex items-center gap-1"><Scale className="w-3 h-3" />Weight</span>
      {MODES.map(([k, l]) => (
        <button key={k} type="button" onClick={() => setMode(k)} data-testid={`wt-sort-${testId}-${k}`}
          className={`px-2 py-1 font-semibold transition-colors ${mode === k ? (k === "weighed" ? "bg-emerald-600 text-white" : k === "latest" ? "bg-slate-700 text-white" : "bg-rose-600 text-white") : "text-slate-600 hover:bg-slate-100"}`}>
          {l}{k === "only" && nMissing > 0 && <span className={`ml-1 rounded-full px-1 ${mode === k ? "bg-white/30" : "bg-rose-100 text-rose-700"}`}>{nMissing}</span>}
        </button>
      ))}
    </div>
  );
};

export const WeightChip = ({ s, testId }) => {
  if (!s || !s.total) return null;
  const cls = s.missing === 0 ? "bg-emerald-100 text-emerald-800 border-emerald-300"
    : s.missing === s.total ? "bg-rose-600 text-white border-rose-600" : "bg-amber-100 text-amber-800 border-amber-300";
  const label = s.missing === 0 ? `${s.total}/${s.total} weighed` : `${s.missing} no-wt · ${s.weighed}/${s.total}`;
  return (
    <span className={`ml-1 inline-flex items-center gap-0.5 whitespace-nowrap rounded-full border px-1.5 text-[9px] font-bold font-sans ${cls}`}
      title={`${s.weighed} of ${s.total} parcels weighed (Database A)`} data-testid={testId}>
      <Scale className="w-2.5 h-2.5" />{label}
    </span>
  );
};
